import React, { useState, useEffect } from 'react';
import styles from './dimension-schema-editor.module.css';

/**
 * DimensionSchemaEditor — Two-column Active / Inactive pool with batch confirm
 *
 * Active column:   dimensions currently on in this session.  ✕ icon → pending deactivate.
 * Inactive column: dimensions in the pool but off.           + icon → pending activate.
 * Card body is informational only (no click toggle); the icons are the affordance.
 *
 * Changes apply only when the user clicks "Confirm". On Confirm, any newly-
 * activated dimensions also get auto-generated (per-dim LLM call) so the user
 * doesn't have to trigger a separate "Re-analyze".
 *
 * "Remove from pool" (trash icon, admin-only) permanently deletes from the pool.
 * Hidden entirely for non-admin roles.
 *
 * Props:
 *   onClose          — close the modal
 *   onSchemaChange   — callback after pool changes
 *   sessionDeviceId  — current session (for activate/deactivate)
 *   onSessionChange  — callback after per-session changes
 *   currentUserRole  — role string ('admin' | 'super' | 'user' | 'study'); used to
 *                      gate the pool-remove affordance. Defaults to non-admin if absent.
 */
const DimensionSchemaEditor = ({ onClose, onSchemaChange, sessionDeviceId, onSessionChange }) => {
    // Role-gate the pool-remove affordance + read CUI 2026 study condition.
    // Defaults: non-admin (hide destructive op) and canEdit=true (preserve
    // default editing behavior if /me lookup fails for non-study users).
    // For `no_edit` study participants the entire editor renders read-only:
    // toggle icons hidden, Confirm hidden, add-dimension hidden.
    const [isAdmin, setIsAdmin] = useState(false);
    const [canEdit, setCanEdit] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/v1/me');
                if (res.ok) {
                    const me = await res.json();
                    setIsAdmin(me.role === 'admin' || me.role === 'super');
                    setCanEdit(me.study_condition !== 'no_edit');
                }
            } catch (_) {}
        })();
    }, []);
    const [pool, setPool] = useState(null);
    const [sessionDims, setSessionDims] = useState(null);   // confirmed truth from server
    const [pendingDims, setPendingDims] = useState(null);   // user's pending selection (Set | null)
    const [error, setError] = useState(null);
    const [isAddingDim, setIsAddingDim] = useState(false);
    const [newDim, setNewDim] = useState({ key: '', name: '', description: '', indicators: '', scoring_criteria: '', color: 'rgba(150, 150, 150, 0.35)' });
    const [isSaving, setIsSaving] = useState(false);
    const [removingDim, setRemovingDim] = useState(null);   // dim key being permanently removed
    const [isApplying, setIsApplying] = useState(false);

    useEffect(() => {
        fetchPool();
        if (sessionDeviceId) fetchSessionDims();
    }, [sessionDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialise pendingDims from sessionDims on first load
    useEffect(() => {
        if (sessionDims !== null && pendingDims === null) {
            setPendingDims(new Set(sessionDims));
        }
    }, [sessionDims, pendingDims]);

    const fetchPool = async () => {
        try {
            const res = await fetch('/api/v1/dimension-schemas/default');
            if (res.ok) { const data = await res.json(); setPool(data.dimensions || []); }
            else setError('Failed to load dimension pool');
        } catch (_) { setError('Failed to load dimension pool'); }
    };

    const fetchSessionDims = async () => {
        if (!sessionDeviceId) return null;
        try {
            const res = await fetch(`/api/v1/seven-cs/results/${sessionDeviceId}`);
            if (res.ok) {
                const data = await res.json();
                const dims = data.summary ? Object.keys(data.summary) : null;
                setSessionDims(dims);
                return dims;
            }
        } catch (_) {}
        return null;
    };

    const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

    const handleNameChange = (val) => {
        const updated = { ...newDim, name: val };
        if (!newDim.key || newDim.key === slugify(newDim.name)) updated.key = slugify(val);
        setNewDim(updated);
    };

    const addToPool = async () => {
        if (!newDim.key || !newDim.name) { setError('Name and key are required'); return; }
        setIsSaving(true);
        setError(null);
        try {
            const body = {
                key: newDim.key,
                name: newDim.name,
                description: newDim.description,
                indicators: newDim.indicators ? newDim.indicators.split(',').map(s => s.trim()).filter(Boolean) : [],
                scoring_criteria: newDim.scoring_criteria,
                color: newDim.color,
            };
            const res = await fetch('/api/v1/dimension-pool/add', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (res.ok) {
                const addedKey = newDim.key;
                setNewDim({ key: '', name: '', description: '', indicators: '', scoring_criteria: '', color: 'rgba(150, 150, 150, 0.35)' });
                setIsAddingDim(false);
                await fetchPool();
                // A newly-added dimension defaults to *active* in pending state.
                // Users add a dimension because they want it; auto-activating it
                // saves a click and matches user intent. The actual activation
                // (and per-dim generation) still requires clicking Confirm.
                if (sessionDeviceId) {
                    setPendingDims(prev => {
                        const next = new Set(prev || []);
                        next.add(addedKey);
                        return next;
                    });
                }
                if (onSchemaChange) onSchemaChange();
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to add dimension');
            }
        } catch (_) { setError('Failed to add dimension'); }
        finally { setIsSaving(false); }
    };

    const removeFromPool = async (dimKey, e) => {
        e.stopPropagation();
        if (!window.confirm(`Remove "${dimKey}" from the pool permanently? This also removes it from ALL sessions.`)) return;
        setRemovingDim(dimKey);
        setError(null);
        try {
            const res = await fetch('/api/v1/dimension-pool/remove', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key: dimKey }),
            });
            if (res.ok) {
                setPendingDims(prev => { if (!prev) return prev; const next = new Set(prev); next.delete(dimKey); return next; });
                await fetchPool();
                if (sessionDeviceId) await fetchSessionDims();
                if (onSchemaChange) onSchemaChange();
                if (onSessionChange) onSessionChange();
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to remove dimension');
            }
        } catch (_) { setError('Failed to remove dimension'); }
        finally { setRemovingDim(null); }
    };

    const toggleDimPending = (dimKey) => {
        setPendingDims(prev => {
            const next = new Set(prev);
            if (next.has(dimKey)) next.delete(dimKey);
            else next.add(dimKey);
            return next;
        });
    };

    const applyChanges = async () => {
        if (!sessionDeviceId || !pendingDims || !sessionDims) return;
        setIsApplying(true);
        setError(null);
        const currentSet = new Set(sessionDims);
        const toActivate = [...pendingDims].filter(k => !currentSet.has(k));
        const toDeactivate = [...currentSet].filter(k => !pendingDims.has(k));
        try {
            // Deactivate first so we free up state and any subsequent activate
            // doesn't trip a "dimension already exists" path in the placeholder.
            for (const k of toDeactivate) {
                const res = await fetch(`/api/v1/seven-cs/results/${sessionDeviceId}/deactivate`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ dimension: k }),
                });
                if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to deactivate'); }
            }
            // For each newly-active dimension: first register it (adds the
            // placeholder), then fire the per-dim assessment so the placeholder
            // gets replaced with real content as part of Confirm. Sequential
            // (not parallel) to avoid hammering OpenAI when a user adds 3-4
            // dimensions at once; per-dim runs are typically a few seconds each.
            for (const k of toActivate) {
                const actRes = await fetch(`/api/v1/seven-cs/results/${sessionDeviceId}/activate`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ dimension: k }),
                });
                if (!actRes.ok) { const d = await actRes.json(); throw new Error(d.error || 'Failed to activate'); }
                const genRes = await fetch(`/api/v1/seven-cs/results/${sessionDeviceId}/analyze-dimension/${k}`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                });
                if (!genRes.ok) {
                    const d = await genRes.json();
                    // Activation succeeded but generation failed — leave the
                    // placeholder in place so the user can retry. Surface the
                    // error but don't roll back the activation.
                    throw new Error(d.error || `Failed to generate ${k}`);
                }
            }
            const newDims = await fetchSessionDims();  // refreshes sessionDims state
            setPendingDims(new Set(newDims || []));     // sync pendingDims to confirmed state
            if (onSessionChange) onSessionChange();
        } catch (e) {
            setError(e.message || 'Failed to apply changes');
        } finally {
            setIsApplying(false);
        }
    };

    const resetPending = () => setPendingDims(sessionDims ? new Set(sessionDims) : new Set());

    const isPendingActive = (dimKey) => pendingDims ? pendingDims.has(dimKey) : (sessionDims && sessionDims.includes(dimKey));
    const hasAnalysis = sessionDims !== null;
    const hasPendingChanges = pendingDims !== null && sessionDims !== null && (
        [...pendingDims].some(k => !sessionDims.includes(k)) ||
        sessionDims.some(k => !pendingDims.has(k))
    );

    // Session dimensions whose key is no longer in the pool (the dimension was
    // removed from the pool while this session's analysis still carries it).
    // Without a synthesized card they render nowhere and can never be
    // deactivated from the UI. Derived from sessionDims ∪ pendingDims so a
    // pending-deactivated orphan stays visible (in Inactive) until Confirm.
    const poolKeys = new Set((pool || []).map(d => d.key));
    const orphanKeys = [...new Set([...(sessionDims || []), ...(pendingDims || [])])]
        .filter(k => !poolKeys.has(k)).sort();
    const orphanDims = orphanKeys.map(k => ({
        key: k,
        name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        indicators: [],
        color: 'rgba(150,150,150,0.35)',
        _orphan: true,
    }));

    // Split pool (plus orphans) by pending selection for the two-column layout
    const activeDims   = [...(pool || []), ...orphanDims].filter(d => isPendingActive(d.key));
    const inactiveDims = [...(pool || []), ...orphanDims].filter(d => !isPendingActive(d.key));

    const renderDimCard = (dim, isActive) => {
        const removing = removingDim === dim.key;
        const dotColor = (dim.color || 'rgba(150,150,150,0.35)').replace('0.35', '1');
        const indicators = Array.isArray(dim.indicators) ? dim.indicators : [];
        const toggleDisabled = removing || isApplying || !hasAnalysis;

        // Card body is inert — the explicit ✕ (active pane) and + (inactive pane)
        // icons in the header are the only interactive affordances for activate/
        // deactivate. The "remove from pool" trash icon (admin-only) is separate
        // and destructive across all sessions.
        return (
            <div
                key={dim.key}
                className={`${styles.dimCard} ${isActive ? styles.dimCardActive : styles.dimCardInactive} ${(removing || isApplying) ? styles.dimCardLoading : ''}`}
            >
                <div className={styles.dimCardHeader}>
                    <span className={styles.dimColorDot} style={{ backgroundColor: dotColor }} />
                    <span className={styles.dimCardName}>{dim.name}</span>
                    {dim._orphan && (
                        <span
                            className={styles.orphanChip}
                            title="This dimension was removed from the pool but is still part of this session's assessment. Deactivate it to remove it from this session."
                        >
                            not in pool
                        </span>
                    )}
                    {hasAnalysis && canEdit && (
                        <button
                            className={styles.toggleBtn}
                            onClick={e => { e.stopPropagation(); if (!toggleDisabled) toggleDimPending(dim.key); }}
                            disabled={toggleDisabled}
                            title={isActive ? 'Deactivate for this session' : 'Activate for this session'}
                            aria-label={isActive ? 'Deactivate' : 'Activate'}
                        >
                            {isActive ? '✕' : '+'}
                        </button>
                    )}
                    {isAdmin && !dim._orphan && (
                        <button
                            className={styles.removeBtn}
                            onClick={e => removeFromPool(dim.key, e)}
                            disabled={removing || isApplying}
                            title="Remove from pool permanently (admin)"
                            aria-label="Remove from pool permanently"
                        >
                            🗑
                        </button>
                    )}
                </div>
                {indicators.length > 0 && (
                    <div className={styles.dimIndicators}>
                        {indicators.map(ind => (
                            <span key={ind} className={styles.dimIndicatorChip}>{ind}</span>
                        ))}
                    </div>
                )}
                {dim.description && (
                    <p className={styles.dimCardDesc}>{dim.description}</p>
                )}
            </div>
        );
    };

    return (
        <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2>Dimension Pool</h2>
                    <button className={styles.closeBtn} onClick={onClose}>✕</button>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {pool === null ? (
                    <div className={styles.placeholder}>Loading…</div>
                ) : (
                    <>
                        {hasAnalysis ? (
                            /* ── Two-column layout when session has an analysis ── */
                            <div className={styles.twoColLayout}>
                                <div className={styles.poolColumn}>
                                    <div className={styles.columnHeader}>
                                        <span className={styles.columnDot} style={{ background: '#22c55e' }} />
                                        Active
                                        <span className={styles.columnCount}>{activeDims.length}</span>
                                    </div>
                                    {activeDims.length === 0 ? (
                                        <div className={styles.emptyCol}>No active dimensions</div>
                                    ) : (
                                        activeDims.map(d => renderDimCard(d, true))
                                    )}
                                </div>

                                <div className={styles.poolColumn}>
                                    <div className={styles.columnHeader}>
                                        <span className={styles.columnDot} style={{ background: '#94a3b8' }} />
                                        Inactive
                                        <span className={styles.columnCount}>{inactiveDims.length}</span>
                                    </div>
                                    {inactiveDims.length === 0 ? (
                                        <div className={styles.emptyCol}>All dimensions active</div>
                                    ) : (
                                        inactiveDims.map(d => renderDimCard(d, false))
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* ── Single-column fallback when no analysis yet ── */
                            <div className={styles.poolList}>
                                {pool.length === 0 ? (
                                    <div className={styles.placeholder}>No dimensions in pool.</div>
                                ) : (
                                    pool.map(dim => {
                                        const indicators = Array.isArray(dim.indicators) ? dim.indicators : [];
                                        return (
                                            <div key={dim.key} className={styles.dimCard}>
                                                <div className={styles.dimCardHeader}>
                                                    <span
                                                        className={styles.dimColorDot}
                                                        style={{ backgroundColor: (dim.color || 'rgba(150,150,150,0.35)').replace('0.35', '1') }}
                                                    />
                                                    <span className={styles.dimCardName}>{dim.name}</span>
                                                    {isAdmin && (
                                                        <button
                                                            className={styles.removeBtn}
                                                            onClick={e => removeFromPool(dim.key, e)}
                                                            title="Remove from pool permanently (admin)"
                                                            aria-label="Remove from pool permanently"
                                                        >
                                                            🗑
                                                        </button>
                                                    )}
                                                </div>
                                                {indicators.length > 0 && (
                                                    <div className={styles.dimIndicators}>
                                                        {indicators.map(ind => (
                                                            <span key={ind} className={styles.dimIndicatorChip}>{ind}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {dim.description && <p className={styles.dimCardDesc}>{dim.description}</p>}
                                            </div>
                                        );
                                    })
                                )}
                                <p className={styles.noAnalysisNote}>
                                    Run an analysis first to activate/deactivate dimensions for this session.
                                </p>
                            </div>
                        )}

                        {/* ── Pending changes bar (hidden in read-only / no_edit) ── */}
                        {canEdit && hasPendingChanges && (
                            <div className={styles.pendingBar}>
                                <span className={styles.pendingLabel}>Unsaved changes</span>
                                <div className={styles.pendingActions}>
                                    <button className={styles.resetBtn} onClick={resetPending} disabled={isApplying}>
                                        Reset
                                    </button>
                                    <button className={styles.applyBtn} onClick={applyChanges} disabled={isApplying}>
                                        {isApplying ? 'Applying…' : 'Confirm'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── Read-only note for no_edit condition ── */}
                        {!canEdit && (
                            <div className={styles.readOnlyNote} style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                                Dimensions are read-only for this session.
                            </div>
                        )}

                        {/* ── Add dimension form (hidden in read-only / no_edit) ── */}
                        <div className={styles.addSection}>
                            {isAddingDim ? (
                                <div className={styles.addForm}>
                                    <h3>Add New Dimension to Pool</h3>
                                    <div className={styles.addFormGrid}>
                                        <div className={styles.formRowFull}>
                                            <label>Name</label>
                                            <input type="text" value={newDim.name} onChange={e => handleNameChange(e.target.value)} className={styles.input} placeholder="e.g. Creativity" />
                                            {newDim.key && (
                                                <span className={styles.keyChip}>
                                                    ID: {newDim.key}
                                                    <button className={styles.keyEditBtn} onClick={() => {
                                                        const custom = window.prompt('Edit dimension ID:', newDim.key);
                                                        if (custom !== null) setNewDim({ ...newDim, key: custom.toLowerCase().replace(/[^a-z0-9]+/g, '_') });
                                                    }}>edit</button>
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.formRowFull}>
                                            <label>Definition</label>
                                            <textarea value={newDim.description} onChange={e => setNewDim({ ...newDim, description: e.target.value })} className={styles.textarea} rows={2} placeholder="What does this dimension measure?" />
                                        </div>
                                        <div className={styles.formRowFull}>
                                            <label>Indicators <span className={styles.optionalLabel}>(optional, comma-separated)</span></label>
                                            <input type="text" value={newDim.indicators} onChange={e => setNewDim({ ...newDim, indicators: e.target.value })} className={styles.input} placeholder="e.g. novel ideas, divergent thinking" />
                                        </div>
                                        <div className={styles.formRowFull}>
                                            <label>Scoring Criteria <span className={styles.optionalLabel}>(optional)</span></label>
                                            <textarea value={newDim.scoring_criteria} onChange={e => setNewDim({ ...newDim, scoring_criteria: e.target.value })} className={styles.textarea} rows={2} placeholder="How should this dimension be scored?" />
                                        </div>
                                    </div>
                                    <div className={styles.addFormActions}>
                                        <button onClick={addToPool} disabled={isSaving} className={styles.saveBtn}>
                                            {isSaving ? 'Adding…' : 'Add to Pool'}
                                        </button>
                                        <button onClick={() => { setIsAddingDim(false); setError(null); }} className={styles.cancelBtn}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (canEdit && (
                                <button onClick={() => setIsAddingDim(true)} className={styles.addDimBtn}>
                                    + Add New Dimension
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DimensionSchemaEditor;
